<script setup lang="ts">
import { computed } from 'vue';
import type { ModelPricing } from '../../types/models';

const props = defineProps<{
  pricing?: ModelPricing;
  task?: string;
}>();

/**
 * Format a unit-cost line based on model task. Returns null when the model
 * carries no cost signal for its task (hide the badge rather than show $0).
 */
const label = computed<string | null>(() => {
  const p = props.pricing;
  if (!p) return null;

  const fmtPer1M = (v?: number) => (v !== undefined && v > 0 ? `$${v.toFixed(2)}/1M` : null);
  const fmtPerChar = (v?: number) => (v !== undefined && v > 0 ? `$${(v * 1_000_000).toFixed(2)}/1M chars` : null);
  const fmtPerMin = (v?: number) => (v !== undefined && v > 0 ? `$${v.toFixed(4)}/min` : null);
  const fmtPerImage = (v?: number) => (v !== undefined && v > 0 ? `$${v.toFixed(3)}/img` : null);

  switch (props.task) {
    case 'embedding': {
      const inC = fmtPer1M(p.inputCostPer1M);
      return inC ? `in ${inC}` : null;
    }
    case 'image_generation': {
      const perImg = fmtPerImage(p.perImageCost);
      return perImg ?? null;
    }
    case 'transcription': {
      return fmtPerMin(p.perAudioMinuteCost);
    }
    case 'tts': {
      return fmtPerChar(p.perCharCost);
    }
    case 'moderation':
    case 'chat':
    default: {
      if (p.pricingTiers && p.pricingTiers.length > 0) {
        const t = p.pricingTiers[0];
        return `in $${t.inputPer1M.toFixed(2)}/1M · out $${t.outputPer1M.toFixed(2)}/1M · tiered`;
      }
      const inC = fmtPer1M(p.inputCostPer1M);
      const outC = fmtPer1M(p.outputCostPer1M);
      if (!inC && !outC) return null;
      return `in ${inC ?? '—'} · out ${outC ?? '—'}`;
    }
  }
});
</script>

<template>
  <span v-if="label" class="pricing-badge" :title="`Cost for this model — ${label}`">
    {{ label }}
  </span>
</template>

<style scoped>
.pricing-badge {
  display: inline-block;
  padding: 1px 6px;
  margin-left: 6px;
  font-size: 11px;
  color: var(--asab-text-muted, #6b7280);
  background: var(--asab-surface-alt, #f3f4f6);
  border-radius: 3px;
  white-space: nowrap;
}
</style>
